/**
 * RoleDetailEditor — Full-page role detail/editor
 *
 * Matches OnMove-Admin RoleDetailEditor:
 *  - Full-width page with DashboardNavbar
 *  - Gradient header card with title, chips, stats, actions
 *  - System role warning alert
 *  - Search bar across all sections
 *  - Collapsible sections: Overview, Permissions, Modules
 *  - Dirty tracking (JSON comparison)
 *  - CASL permission checks for read/update/delete
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";

import KDButton from "components/KDButton";
import FullPageDetailTemplate from "templates/FullPageDetailTemplate";
import OverviewSection from "./sections/OverviewSection";
import PermissionsSection from "./sections/PermissionsSection";
import ModulesSection from "./sections/ModulesSection";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import useRoles from "hooks/useRoles";
import logger from "services/logger";

function RoleDetailEditor() {
  const { id: roleId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const ability = useAbility();
  const { getRole, updateRole, deleteRole } = useRoles();

  // Role data
  const [roleData, setRoleData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    permissions: false,
    modules: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", title: "", message: "" });

  // Permission checks
  const canRead = ability.can(ACTIONS.READ, SUBJECTS.ROLE);
  const canUpdate = ability.can(ACTIONS.MANAGE, SUBJECTS.ROLE) || ability.can(ACTIONS.UPDATE, SUBJECTS.ROLE);
  const canDelete = ability.can(ACTIONS.MANAGE, SUBJECTS.ROLE) || ability.can(ACTIONS.DELETE, SUBJECTS.ROLE);

  // Dirty tracking
  const hasChanges = useMemo(() => {
    if (!roleData || !originalData) return false;
    return JSON.stringify(roleData) !== JSON.stringify(originalData);
  }, [roleData, originalData]);

  // Load
  const loadRole = useCallback(async () => {
    if (!roleId) return;
    setLoading(true);
    try {
      const data = await getRole(roleId);
      if (!data) throw new Error("Role not found");

      // Ensure arrays
      const normalized = {
        ...data,
        permissions: Array.isArray(data.permissions) ? data.permissions : [],
        modules: Array.isArray(data.modules) ? data.modules : [],
        accessLevels: Array.isArray(data.accessLevels) ? data.accessLevels : [],
      };
      setRoleData(normalized);
      setOriginalData(JSON.parse(JSON.stringify(normalized)));
    } catch (err) {
      logger.error("[RoleDetailEditor]", "Load failed:", err.message);
      showSnackbar("error", t("common.error"), err.message);
      setRoleData(null);
    } finally {
      setLoading(false);
    }
  }, [roleId, getRole, t]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  // Field update
  const updateField = useCallback((field, value) => {
    setRoleData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Save
  const handleSave = async () => {
    if (!roleData || !hasChanges) return;
    setSaving(true);
    try {
      await updateRole(roleId, {
        name: roleData.name,
        description: roleData.description,
        color: roleData.color,
        order: roleData.order,
        isActive: roleData.isActive,
        modules: roleData.modules,
        permissions: roleData.permissions,
        accessLevels: roleData.accessLevels,
      });
      setOriginalData(JSON.parse(JSON.stringify(roleData)));
      showSnackbar("success", t("common.success"), t("roles.detail.saved"));
    } catch (err) {
      logger.error("[RoleDetailEditor]", "Save failed:", err.message);
      showSnackbar("error", t("common.error"), err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset
  const handleReset = () => {
    setRoleData(JSON.parse(JSON.stringify(originalData)));
  };

  // Delete
  const handleDelete = async () => {
    if (roleData?.isSystem) {
      showSnackbar("error", t("common.error"), t("roles.detail.cannotDeleteSystem"));
      return;
    }
    if (!window.confirm(t("editor.confirmDelete"))) return;
    try {
      await deleteRole(roleId);
      showSnackbar("success", t("common.success"), t("roles.detail.deleted"));
      navigate("/admin/roles");
    } catch (err) {
      logger.error("[RoleDetailEditor]", "Delete failed:", err.message);
      showSnackbar("error", t("common.error"), err.message);
    }
  };

  // Section toggle
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Snackbar
  const showSnackbar = (severity, title, message) => {
    setSnackbar({ open: true, severity, title, message });
  };

  // Build chips
  const chips = useMemo(() => {
    if (!roleData) return [];
    const c = [];
    c.push({ label: roleData.id || roleId, bgcolor: roleData.color || "rgba(255,255,255,0.2)" });
    if (roleData.isSystem) c.push({ label: t("roles.system"), bgcolor: "rgba(245,158,11,0.25)", color: "#FDE047" });
    if (roleData.isActive === false) c.push({ label: t("common.inactive"), bgcolor: "rgba(239,68,68,0.25)", color: "#FCA5A5" });
    return c;
  }, [roleData, roleId, t]);

  // Build stats
  const stats = useMemo(() => {
    if (!roleData) return [];
    return [
      { label: t("roles.permissions"), value: roleData.permissions?.length || 0 },
      { label: t("roles.modules"), value: roleData.modules?.length || 0 },
      { label: t("roles.accessLevels"), value: roleData.accessLevels?.length || 0 },
      { label: t("roles.detail.order"), value: roleData.order || 0 },
    ];
  }, [roleData, t]);

  // Build alerts
  const alerts = useMemo(() => {
    if (!roleData?.isSystem) return [];
    return [
      {
        severity: "warning",
        content: (
          <>
            <strong>{t("roles.system")}:</strong> {t("roles.detail.systemWarning")}
          </>
        ),
      },
    ];
  }, [roleData, t]);

  // Build actions
  const actionButtons = (
    <>
      <Tooltip title={t("roles.detail.refresh")}>
        <IconButton onClick={loadRole} sx={{ color: "white" }}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>

      {canUpdate && hasChanges && (
        <>
          <KDButton
            variant="outlined"
            size="small"
            onClick={handleReset}
            sx={{
              color: "white",
              borderColor: "rgba(255,255,255,0.4)",
              "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            {t("roles.detail.discard")}
          </KDButton>
          <KDButton
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
            }}
          >
            {saving ? (
              <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
            ) : (
              <SaveIcon sx={{ fontSize: 18, mr: 0.5 }} />
            )}
            {saving ? t("common.loading") : t("common.save")}
          </KDButton>
        </>
      )}

      {canDelete && !roleData?.isSystem && (
        <Tooltip title={t("common.delete")}>
          <IconButton
            onClick={handleDelete}
            sx={{ color: "#FCA5A5", "&:hover": { color: "#EF4444" } }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <FullPageDetailTemplate
      title={roleData?.name || roleData?.label || roleId || ""}
      subtitle={roleData?.description || ""}
      titleIcon={roleData?.isSystem ? <LockIcon sx={{ color: "#FDE047", fontSize: 24 }} /> : null}
      chips={chips}
      stats={stats}
      headerColor="info"
      backRoute="/admin/roles"
      loading={loading}
      notFound={!loading && !roleData}
      notFoundMessage={t("roles.detail.notFound")}
      permissionDenied={!canRead}
      alerts={alerts}
      searchPlaceholder={t("roles.detail.searchPlaceholder")}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      actions={roleData ? actionButtons : null}
      snackbar={snackbar}
      onSnackbarClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
    >
      {roleData && (
        <>
          <OverviewSection
            roleData={roleData}
            updateField={updateField}
            expanded={expandedSections.overview}
            onToggle={() => toggleSection("overview")}
            canEdit={canUpdate}
          />

          <PermissionsSection
            roleData={roleData}
            updateField={updateField}
            expanded={expandedSections.permissions}
            onToggle={() => toggleSection("permissions")}
            canEdit={canUpdate}
            searchQuery={searchQuery}
          />

          <ModulesSection
            roleData={roleData}
            updateField={updateField}
            expanded={expandedSections.modules}
            onToggle={() => toggleSection("modules")}
            canEdit={canUpdate}
            searchQuery={searchQuery}
          />
        </>
      )}
    </FullPageDetailTemplate>
  );
}

export default RoleDetailEditor;
